import { parseDynamicData } from '$shared/helpers/utils';
import { NpiBasicModel } from './NpiBasicModel';
import * as ListTypes from '$shared/atoms/ListTypes/index';
import { NpiOtherName } from './NpiOtherName';
import { NpiProfileAddress } from './NpiProfileAddress';
import { DatasetMatchRecord } from '../DatasetMatchRecord';
import { EntityParameterType } from '$shared/constants/EntityParameterType';

const InterceptRecordKeys = {
  Basic: 'basic',
  Addresses: 'addresses',
  PracticeLocations: 'practiceLocations',
  OtherNames: 'otherNames',
  Identifiers: 'identifiers',
  Taxonomies: 'taxonomies'
};

export class NpiDatasetMatchRecord extends DatasetMatchRecord {
  private readonly IgnoreKeys = [
    'id',
    'hash',
    'firstName',
    'lastName',
    'prefix',
    'suffix',
    'npiNumber'
  ];

  public get npiNumber(): string {
    return this.record?.npiNumber || '';
  }

  public get basicDetails(): NpiBasicModel {
    return this.record?.basic && new NpiBasicModel(this.record.basic);
  }

  public get otherNames(): NpiOtherName[] {
    return (
      (Array.isArray(this.record?.otherNames) &&
        this.record.otherNames.map((otherName) => new NpiOtherName(otherName))) ||
      []
    );
  }

  public get practiceLocations(): NpiProfileAddress[] {
    return (
      (Array.isArray(this.record?.practiceLocations) &&
        this.record.practiceLocations.map(
          (practiceLocation) => new NpiProfileAddress(practiceLocation)
        )) ||
      []
    );
  }

  public get addresses(): NpiProfileAddress[] {
    return (
      (Array.isArray(this.record?.addresses) &&
        this.record.addresses.map((address) => new NpiProfileAddress(address))) ||
      []
    );
  }

  public get isDeactived(): boolean {
    return !!this.record?.isDeactivated;
  }

  public override getName(): string {
    return this.basicDetails?.getName() || super.getName();
  }

  public toList(): IDataList {
    let tempResult: IDataList = {};

    Object.entries(this.record).forEach(([key, value]) => {
      if (this.IgnoreKeys.includes(key)) return;

      if (key === InterceptRecordKeys.Basic) {
        tempResult = { ...tempResult, ...this.basicDetails.toList() };
        return;
      }
      if (key === InterceptRecordKeys.Addresses) {
        this.addresses.forEach((address: NpiProfileAddress, index: number) => {
          tempResult[`${address.purpose}Address@@${index}`] = address.toList();
        });
        return;
      }
      if (key === InterceptRecordKeys.PracticeLocations) {
        this.practiceLocations.forEach((address: NpiProfileAddress, index: number) => {
          tempResult[`${address.purpose}PracticeLocation@@${index}`] = address.toList();
        });
        return;
      }
      if (key === InterceptRecordKeys.OtherNames) {
        tempResult[key] = {
          component: ListTypes.SimpleArrayListType,
          props: { value: this.otherNames.map((name) => name.toString()) }
        };
        return;
      }
      if (key === InterceptRecordKeys.Identifiers) {
        tempResult[key] = {
          component: ListTypes.ArrayObjectListType,
          props: { value: this.record?.identifiers || [] }
        };
        return;
      }
      if (key === InterceptRecordKeys.Taxonomies) {
        tempResult[key] = {
          component: ListTypes.ArrayObjectListType,
          props: { value: this.record?.taxonomies || [] }
        };
        return;
      }
      const parsedValue = parseDynamicData(value);
      tempResult[key] = parsedValue;
    });

    return { name: this.basicDetails?.getName?.() || '', npiNumber: this.npiNumber, ...tempResult };
  }

  protected getResultKeyByType(type: EntityParameterType): string | undefined {
    if (type === EntityParameterType.FacilityName) {
      return 'name';
    }
    if (type === EntityParameterType.ProviderNpi) {
      return 'npiNumber';
    }
  }
}
